import { createTheme, alpha } from '@mui/material/styles';

/**
 * NSG brand theme — black / red / white security dashboard.
 *
 * Dark-first with NSG red accent. Outfit (geometric sans) for UI text,
 * IBM Plex Mono for data. MUI v7 colorSchemes API for light/dark toggle.
 */

const FONT_DISPLAY = '"Outfit", "Helvetica Neue", sans-serif';
const FONT_MONO = '"IBM Plex Mono", "Fira Code", monospace';

// ── Palette tokens ──────────────────────────────────────────────────────────────

const nsgRed = {
  50: '#fef2f2',
  100: '#fee2e2',
  200: '#fecaca',
  300: '#fca5a5',
  400: '#f87171',
  500: '#ef4444',
  600: '#dc2626',
  700: '#b91c1c',
  800: '#991b1b',
  900: '#7f1d1d',
};

const neutral = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#e5e5e5',
  300: '#d4d4d4',
  400: '#a3a3a3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
  950: '#0a0a0a',
};

const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data' },
  colorSchemes: {
    dark: {
      palette: {
        primary: { main: nsgRed[600], light: nsgRed[400], dark: nsgRed[800] },
        secondary: { main: '#818cf8' },
        error: { main: '#f87171', light: '#fca5a5', dark: '#dc2626' },
        warning: { main: '#fbbf24', light: '#fde68a', dark: '#d97706' },
        success: { main: '#22c55e', light: '#86efac', dark: '#16a34a' },
        info: { main: '#60a5fa', light: '#93c5fd', dark: '#2563eb' },
        background: {
          default: neutral[950],
          paper: '#141414',
        },
        text: {
          primary: '#f1f1f1',
          secondary: neutral[400],
        },
        divider: alpha(neutral[400], 0.12),
        action: {
          hover: alpha(neutral[400], 0.06),
          selected: alpha(nsgRed[600], 0.1),
        },
      },
    },
    light: {
      palette: {
        primary: { main: nsgRed[700], light: nsgRed[500], dark: nsgRed[800] },
        secondary: { main: '#6366f1' },
        error: { main: '#dc2626' },
        warning: { main: '#d97706' },
        success: { main: '#16a34a' },
        info: { main: '#2563eb' },
        background: {
          default: '#fafafa',
          paper: '#ffffff',
        },
        text: {
          primary: neutral[900],
          secondary: neutral[500],
        },
        divider: alpha(neutral[300], 0.5),
      },
    },
  },
  defaultColorScheme: 'dark',
  typography: {
    fontFamily: FONT_DISPLAY,
    h3: { fontWeight: 700, letterSpacing: '-0.03em', fontFamily: FONT_DISPLAY },
    h4: { fontWeight: 700, letterSpacing: '-0.02em', fontFamily: FONT_DISPLAY },
    h5: { fontWeight: 600, letterSpacing: '-0.01em', fontFamily: FONT_DISPLAY },
    h6: { fontWeight: 600, fontFamily: FONT_DISPLAY },
    subtitle1: { fontWeight: 500, fontFamily: FONT_DISPLAY },
    subtitle2: { fontWeight: 500, fontFamily: FONT_DISPLAY },
    overline: {
      fontWeight: 600,
      letterSpacing: '0.1em',
      fontSize: '0.65rem',
      fontFamily: FONT_DISPLAY,
    },
    button: { fontWeight: 600, letterSpacing: '0.02em', fontFamily: FONT_DISPLAY },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--font-mono': FONT_MONO,
        },
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 6, height: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 3,
            backgroundColor: alpha(neutral[400], 0.3),
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
        sizeSmall: { fontSize: '0.7rem', height: 22 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, minHeight: 44 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 6 },
        bar: { borderRadius: 4 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: FONT_DISPLAY,
          fontWeight: 600,
          textTransform: 'uppercase' as const,
          fontSize: '0.68rem',
          letterSpacing: '0.06em',
        },
      },
    },
  },
});

export default theme;
export { FONT_MONO };
